/**
 * Who may open the Kronos report page and pull the Clockify-format
 * "Detailed Report" export (the file Damien produces for Finance).
 *
 * Roles first: `admin` and `finance` — both already allowed to list every
 * user's time entries server-side (kronos-api listScopes `isPrivileged`), so
 * the export exposes nothing the page doesn't already show them.
 * The e-mail allowlist stays for the named people who hold neither role:
 * Damien (reporting owner), Finance, and IT.
 */
export const REPORT_EXPORT_ALLOWLIST = [
  'dparent@altyor.com',   // Damien — reporting owner
  'efiguie@altyor.com',   // testing
  'cauberger@altyor.com',
  'ilebonte@altyor.com',
  'mfrebourg@altyor.com',
  'alutun@altyor.com',    // IT super admin
] as const;

const ALLOWED = new Set<string>(REPORT_EXPORT_ALLOWLIST);

export interface ReportViewer {
  email?: string | null;
  roles?: readonly string[];
}

const isAllowed = (email: string | null | undefined) =>
  !!email && ALLOWED.has(email.trim().toLowerCase());

/**
 * `user.email` is the Firestore `users/{oid}` field; `account.email` is the
 * token's `preferred_username`. They can disagree (or the doc one can be
 * empty), so the allowlist accepts either — the token is the one that
 * actually identifies the person signing in.
 */
export function canExportDetailedReport(
  user: ReportViewer | null | undefined,
  account?: { email?: string | null } | null,
): boolean {
  if (user?.roles?.some((r) => r === 'admin' || r === 'finance')) return true;
  return isAllowed(user?.email) || isAllowed(account?.email);
}
